import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';



const ReportFilterBar = ({ filters, onChange, onGenerate, reportType, showReportView }) => {
  const [portfolioOptions, setPortfolioOptions] = useState([{ value: '', label: 'All Portfolios' }]);

  useEffect(() => {
    async function fetchPortfolios() {
      try {
        const res = await axios.get(`${API_BASE_URL}/portfolios`);
        const data = res.data && res.data.data ? res.data.data : res.data;
        setPortfolioOptions([{ value: '', label: 'All Portfolios' }, ...(data || []).map(p => ({
          value: p.id || p.portfolio_id || p.name || p.portfolio_name,
          label: p.name || p.portfolio_name
        }))]);
      } catch (e) {
        // Optionally set an error state
      }
    }
    fetchPortfolios();
  }, []);

  const [isinOptions, setIsinOptions] = useState([]);
  const [isinLoading, setIsinLoading] = useState(true);
  const [isinError, setIsinError] = useState('');

  useEffect(() => {
    async function fetchIsins() {
      setIsinLoading(true);
      setIsinError('');
      try {
        const res = await axios.get(`${API_BASE_URL}/isin-master`);
        const data = res.data && res.data.data ? res.data.data : res.data;
        setIsinOptions((data || []).map(i => ({
          value: i.isin || i.isin_number,
          label: `${i.isin || i.isin_number}${i.description ? ' - ' + i.description : (i.isin_issuer ? ' - ' + i.isin_issuer : '')}`
        })));
      } catch (e) {
        setIsinError(e.message || 'Error fetching ISINs');
      } finally {
        setIsinLoading(false);
      }
    }
    fetchIsins();
  }, []);

  const [localFilters, setLocalFilters] = useState(filters || {});

  useEffect(() => {
    setLocalFilters(filters || {});
  }, [filters]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLocalFilters(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'view' && value === 'transactions') {
        next.valueDate = '';
        const seed = next.asAtDate || '';
        if (!next.dateFrom) next.dateFrom = seed;
        if (!next.dateTo) next.dateTo = seed;
      }
      return next;
    });
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerate = () => {
    onChange && onChange(localFilters);
    onGenerate && onGenerate(localFilters);
  };

  // Mark to Market has its own two views: market prices (default) and the portfolio
  // valuation in Finance's layout.
  const isMarkToMarket = reportType === 'mark-to-market';
  const defaultView = isMarkToMarket ? 'prices' : 'positions';
  const isTransactions = (localFilters.view || defaultView) === 'transactions';

  return (
    <div className="flex flex-wrap gap-4 items-end mb-4 bg-white border border-gray-200 rounded-lg shadow-soft p-4">
      {/* Report view: holdings vs transactions (product reports) */}
      {showReportView && (
        <div className="min-w-[180px] flex flex-col gap-2">
          <label className="text-xs font-semibold text-gray-500">Report</label>
          <select
            className="min-h-[44px] h-11 leading-normal border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            name="view"
            value={localFilters.view || defaultView}
            onChange={handleInputChange}
          >
            {isMarkToMarket ? (
              <>
                <option value="prices">Market Prices</option>
                <option value="portfolio">Portfolio Mark to Market</option>
              </>
            ) : (
              <>
                <option value="positions">Holdings (as at)</option>
                <option value="transactions">Transactions</option>
              </>
            )}
          </select>
        </div>
      )}
      {/* As At Date (holdings) or Date From / Date To (transactions) */}
      {!isTransactions && (
      <div className="min-w-[180px] flex flex-col gap-2">
        <label className="text-xs font-semibold text-gray-500">As at Date</label>
        <input
          className="min-h-[44px] h-11 leading-normal border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          type="date"
          name="asAtDate"
          value={localFilters.asAtDate || ''}
          onChange={handleDateChange}
        />
      </div>
      )}
      {isTransactions && (
      <>
      <div className="min-w-[180px] flex flex-col gap-2">
        <label className="text-xs font-semibold text-gray-500">Date From</label>
        <input
          className="min-h-[44px] h-11 leading-normal border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          type="date"
          name="dateFrom"
          value={localFilters.dateFrom || ''}
          onChange={handleDateChange}
        />
      </div>
      <div className="min-w-[180px] flex flex-col gap-2">
        <label className="text-xs font-semibold text-gray-500">Date To</label>
        <input
          className="min-h-[44px] h-11 leading-normal border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          type="date"
          name="dateTo"
          value={localFilters.dateTo || ''}
          onChange={handleDateChange}
        />
      </div>
      </>
      )}
      {(
      <>
      {/* Portfolio */}
      <div className="min-w-[180px] flex flex-col gap-2">
        <label className="text-xs font-semibold text-gray-500">Portfolio</label>
        <select
          className="min-h-[44px] h-11 leading-normal border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          name="portfolio"
          value={localFilters.portfolio || ''}
          onChange={handleInputChange}
        >
          {portfolioOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      {/* ISIN */}
      <div className="min-w-[180px] flex flex-col gap-2">
        <label className="text-xs font-semibold text-gray-500">ISIN</label>
        <select
          className="min-h-[44px] h-11 leading-normal border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          name="isin"
          value={localFilters.isin || ''}
          onChange={handleInputChange}
        >
          <option value="">All ISINs</option>
          {isinLoading ? (
            <option value="" disabled>Loading ISINs...</option>
          ) : isinError ? (
            <option value="" disabled>{isinError}</option>
          ) : isinOptions && isinOptions.length > 0 ? (
            isinOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))
          ) : (
            <option value="" disabled>No ISINs available</option>
          )}
        </select>
      </div>
      {reportType === 'mark-to-market' && (
        <div className="min-w-[180px] flex flex-col gap-2">
          <label className="text-xs font-semibold text-gray-500">Instrument</label>
          <select
            className="min-h-[44px] h-11 leading-normal border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            name="instrumentType"
            value={localFilters.instrumentType || ''}
            onChange={handleInputChange}
          >
            <option value="">All instruments</option>
            <option value="T_BOND">T-Bond</option>
            <option value="T_BILL">T-Bill</option>
          </select>
        </div>
      )}
      {/* Value Date — holdings only (transactions uses Transaction Date) */}
      {!isTransactions && (
      <div className="min-w-[180px] flex flex-col gap-2">
        <label className="text-xs font-semibold text-gray-500">Value Date</label>
        <input
          className="min-h-[44px] h-11 leading-normal border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          type="date"
          name="valueDate"
          value={localFilters.valueDate || ''}
          onChange={handleDateChange}
        />
      </div>
      )}
      {/* Maturity Date */}
      <div className="min-w-[180px] flex flex-col gap-2">
        <label className="text-xs font-semibold text-gray-500">Maturity Date</label>
        <input
          className="min-h-[44px] h-11 leading-normal border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          type="date"
          name="maturityDate"
          value={localFilters.maturityDate || ''}
          onChange={handleDateChange}
        />
      </div>
      {reportType === 'buyback' && (
        <div className="min-w-[180px] flex flex-col gap-2">
          <label className="text-xs font-semibold text-gray-500">Transaction Type</label>
          <select
            className="min-h-[44px] h-11 leading-normal border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            name="transactionPair"
            value={localFilters.transactionPair || ''}
            onChange={handleInputChange}
          >
            <option value="">All (Sell/Buy & Buy/Sell)</option>
            <option value="sell_buy">Sell/Buy</option>
            <option value="buy_sell">Buy/Sell</option>
          </select>
        </div>
      )}
      </>
      )}
      {/* Generate Button */}
      <div className="min-w-[180px] flex flex-col gap-2 justify-end">
        <button
          type="button"
          onClick={handleGenerate}
          className="px-4 py-2 bg-primary text-white font-medium rounded-md hover:bg-primary-dark transition-colors"
        >
          Generate Report
        </button>
      </div>
      {showReportView && isTransactions && (
        <div className="w-full text-xs text-gray-500">
          Transactions lists booked deals by value date (Buy and Sell) in the selected range, including matured and closed deals. Maturity events are not listed as rows.
        </div>
      )}
    </div>
  );
};

export default ReportFilterBar;
